import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Chip,
    Tooltip,
    Skeleton,
} from '@mui/material';
import {
    LocalFireDepartment as FireIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { gamificationService, UserStreak } from '../../services/gamificationService';

interface StreakDisplayProps {
    compact?: boolean;
    showRank?: boolean;
}

const StreakDisplay: React.FC<StreakDisplayProps> = ({
    compact = false,
    showRank = false,
}) => {
    const translate = useTranslate();
    const [streak, setStreak] = useState<UserStreak | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadStreak = async () => {
            try {
                const data = await gamificationService.getMyStreak();
                setStreak(data);
            } catch (error) {
                console.error('Failed to load streak:', error);
            } finally {
                setLoading(false);
            }
        };

        loadStreak();
    }, []);

    if (loading) {
        return <Skeleton variant="rectangular" width={100} height={32} />;
    }

    if (!streak) {
        return null;
    }

    const { currentStreak, longestStreak, isAtRisk, rank } = streak;

    if (compact) {
        return (
            <Tooltip
                title={
                    isAtRisk
                        ? translate('gamification.streakAtRisk')
                        : translate('gamification.streakTooltip', { days: currentStreak })
                }
            >
                <Chip
                    icon={isAtRisk ? <WarningIcon /> : <FireIcon />}
                    label={currentStreak}
                    color={isAtRisk ? 'warning' : currentStreak > 0 ? 'error' : 'default'}
                    size="small"
                    sx={{
                        fontWeight: 'bold',
                        animation: isAtRisk ? 'pulse 1s infinite' : 'none',
                        '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.6 },
                        },
                    }}
                />
            </Tooltip>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: isAtRisk ? 'warning.light' : 'background.paper',
                border: 1,
                borderColor: isAtRisk ? 'warning.main' : 'divider',
            }}
        >
            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: currentStreak > 0 ? 'error.main' : 'grey.300',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <FireIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>

            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" fontWeight="bold">
                    {currentStreak} {translate('gamification.days')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {translate('gamification.currentStreak')}
                </Typography>
                {isAtRisk && (
                    <Typography variant="caption" color="warning.dark">
                        <WarningIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                        {translate('gamification.streakAtRiskMessage')}
                    </Typography>
                )}
            </Box>

            <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                    {translate('gamification.longestStreak')}
                </Typography>
                <Typography variant="h6" fontWeight="medium">
                    {longestStreak} {translate('gamification.days')}
                </Typography>
                {showRank && rank > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        #{rank} {translate('gamification.rank')}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default StreakDisplay;
